import { css, cx } from '@linaria/core'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedProductFamilyId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import type { OptionChangeEntry } from '#app/store/builders/configChangeTracker'
import ArrowRightIcon from '#icons/arrow-right-48.svg'
import ErrorIcon from '#icons/close-cirlce-48.svg'
import RemoveIcon from '#icons/minus-48.svg'
import AdditionIcon from '#icons/plus-48.svg'
import { fragments, style } from '#styles'
import { formatDuration } from '#utils/formatDuration'
import { isVariantRemoval } from '#utils/isVariantRemoval'

const detailListClass = css`
  display: grid;
  grid-template-columns: max-content auto auto max-content max-content max-content auto max-content;
  grid-template-rows: fit-content;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  row-gap: 4px;
  padding: 0 0 148px 0;

  ${fragments.textStyle.body.m.regular}
  > ._row > * {
    padding: 9px 4px 10px 4px;
    color: ${style.themeColors.textDefault};
    border-bottom: 1px solid ${style.themeColors.lineDefault};
  }

  > ._row > :first-child {
    padding-left: 16px;
  }

  > ._row > :last-child {
    padding-right: 24px;
  }

  > ._gap {
    grid-column: 1/9;
    height: 20px;
  }

  > ._heading {
    grid-column: 1/9;
    position: sticky;
    z-index: 1;
    top: 0;
    padding: 32px 5px 10px 15px;
    ${fragments.textStyle.heading.s.regular}
    color: ${style.themeColors.textDeselected};
    background: ${style.themeColors.backgroundDefault};
    border-bottom: 1px solid ${style.themeColors.lineDefault};

    > ._icon {
      margin-right: 10px;
    }
  }

  // last entry is just an empty entry to push the table size a bit

  > ._end {
    grid-column: 1/9;
    height: 148px;
  }

  > ._row {
    display: contents;

    &._deemphasized > * {
      color: ${style.themeColors.textDeselected};
    }

    &._emphasized > * {
      color: ${style.themeColors.textImportant};

      > div {
        background: ${style.themeColors.backgroundAccent} !important;
      }
    }

    &._conflict > * {
      color: ${style.themeColors.textImportant};
    }

    &._lowered {
      color: ${style.themeColors.textDisabled};
    }

    > * {
      display: flex;
      align-items: center;
    }

    > ._family-id {
      ${fragments.textStyle.body.m.regular}
    }

    > ._family-title {
      ${fragments.textStyle.body.m.bold}

      > ._time-stamp {
        margin-left: 4px;
        ${fragments.textStyle.body.m.regular};
        color: ${style.themeColors.textDisabled};
      }
    }

    > ._previous-variant {
      justify-content: end;
    }

    > ._selection-prev {
      justify-content: end;
      height: auto;

      ${fragments.textStyle.body.m.regular};
    }

    > ._selection-icon {
      padding: 4px 0px;
      justify-content: end;

      // redoing icons, because mask inversion and round dots make it very unusable for the icon
      // maybe it should be a property in <Icon />, but future you has to decide.

      ._icon {
        width: 14px;
        height: 14px;
        margin: 2px 0 0 0;
        color: ${style.themeColors.textPrimary};
        background: ${style.themeColors.textDisabled};
        border-radius: 50%;

        &::after {
          content: '';
          display: inline-block;
          // looks funny, maybe needs to be replaced with a better element
          margin: 1px 2px 3px 1px;
          width: 12px;
          height: 12px;
          mask-size: cover;
          background: #fff;
        }

        &._changed::after {
          mask-image: url(${ArrowRightIcon});
        }

        &._addition::after {
          mask-image: url(${AdditionIcon});
        }

        &._removal::after {
          mask-image: url(${RemoveIcon});
        }
        
        &._error::after {
          mask-image: url(${ErrorIcon});
        }

        &._conflict {
          color: ${style.colors.white};
          background: ${style.themeColors.backgroundAccent};
        }

        &._removal {
          background: ${style.themeColors.textDeselected};
        }
      }
    }

    > ._selection-post {
      ${fragments.textStyle.body.m.regular};
    }

    > ._current-variant {
      ${fragments.textStyle.body.m.bold};
    }

    > ._price-difference {
      justify-content: end;
      ${fragments.textStyle.body.m.regular};

      &._positive {
        color: ${style.themeColors.textImportant};
      }

      &._negative {
        color: ${style.themeColors.textDeselected};
      }
    }
  }
`

const ChangeRow: FC<{
  family?: {
    id: EnhancedProductFamilyId
    title?: string
  }
  fromVariant?: {
    id: EnhancedProductVariantId
    title: string
    price: number
  }
  toVariant?: {
    id: EnhancedProductVariantId
    title: string
    price: number
  }
  lastSelectionTime?: Timestamp
  emphasized?: boolean
  conflict?: boolean
  error?: boolean
}> = ({ family, fromVariant, toVariant, lastSelectionTime, emphasized, conflict, error }) => {
  let changeType: '_changed' | '_removal' | '_addition' = fromVariant && toVariant ? '_changed' : fromVariant ? '_removal' : '_addition'
  changeType = toVariant && isVariantRemoval(toVariant.id) ? '_removal' : changeType
  changeType = fromVariant && isVariantRemoval(fromVariant.id) ? '_addition' : changeType
  const priceDifference = (toVariant?.price ?? 0) - (fromVariant?.price ?? 0)
  const fromLowered = !fromVariant || isVariantRemoval(fromVariant.id)
  const toLowered = !toVariant || isVariantRemoval(toVariant.id)

  return (
    <div
      className={cx('_row', changeType === '_removal' && '_deemphasized', emphasized && '_emphasized', conflict && '_conflict', error && '_error')}
    >
      <div className="_family-id _lowered">{family?.id}</div>
      <div className="_family-title">
        {family?.title ?? '—'}
        {lastSelectionTime && <span className="_time-stamp">({formatDuration(Date.now() - lastSelectionTime)})</span>}
      </div>
      <div className={cx('_previous-variant', fromLowered && '_lowered')}>{fromVariant?.title ?? 'N/A'}</div>
      <div className={cx('_selection-prev', fromLowered && '_lowered')}>{fromVariant?.id ?? '-'}</div>
      <div className="_selection-icon">
        <div className={cx('_icon', changeType, conflict && '_conflict', error && '_error')} />
      </div>
      <div className={cx('_selection-post', toLowered && '_lowered')}>{toVariant?.id ?? '-'}</div>
      <div className={cx('_current-variant', toLowered && '_lowered')}>{toVariant?.title ?? 'N/A'}</div>
      <div className={cx('_price-difference', priceDifference > 0 ? '_positive' : '_negative')}>
        {priceDifference.toLocaleString('en-US', { signDisplay: 'exceptZero' })}
      </div>
    </div>
  )
}

export const HistoryDetailList: FC<{ entry: OptionChangeEntry }> = ({ entry }) => {
  const { t } = useTranslation()

  const {
    additions = [],
    changes = [],
    removals = [],
  } = entry.status === 'success' ? entry.consequences : { additions: [], changes: [], removals: [] }
  const conflicts = entry.status === 'success' ? entry.conflicts : []
  const consequenceCount = additions.length + changes.length + removals.length
  const conflictCount = conflicts.length
  const hasError = entry.status === 'failed-unresolved' || entry.status === 'failed-resolved'

  return (
    <div className={detailListClass}>
      {/*<div className="_heading">{t('history.details.title.selected-change', 'Selected change')}</div>*/}
      {entry.selections.map((entry) => (
        <ChangeRow
          key={entry.family.id}
          family={entry.family}
          fromVariant={entry.fromVariant}
          toVariant={entry.toVariant}
          emphasized
          conflict={conflictCount > 0}
        />
      ))}
      {conflictCount > 0 && (
        <>
          <div className="_heading">{conflicts.length} resulting conflicts with your earlier choices</div>
          {conflicts.map((x) => (
            <ChangeRow
              key={x.family.id}
              family={x.family}
              fromVariant={x.fromVariant}
              toVariant={x.archetype === 'change' ? x.toVariant : undefined}
              lastSelectionTime={x.lastSelectionTime}
              conflict
            />
          ))}
        </>
      )}
      {(changes.length > 0 || additions.length > 0) && (
        <>
          <div className="_heading">
            {t('history.details.title.variant-changes', {
              defaultValue_one: 'one Variant Change',
              defaultValue_other: '{{count}} Variant Changes',
              count: consequenceCount,
            })}
          </div>

          {changes.map((x) => (
            <ChangeRow {...x} key={`${x.family.id}${x.fromVariant.id}-${x.family.id}${x.toVariant.id}`} error={hasError} />
          ))}

          {additions.map((x) => (
            <ChangeRow {...x} key={`${x.family.id}${x.toVariant.id}`} error={hasError} />
          ))}
        </>
      )}

      {removals.length > 0 && (
        <>
          <div className="_heading">
            {t('history.details.title.obsolete-changes', {
              defaultValue_one: 'one obsolete family',
              defaultValue_other: '{{count}} obsolete families',
              count: removals.length,
            })}
          </div>
          {removals.map((x) => (
            <ChangeRow {...x} key={`${x.family.id}${x.fromVariant.id}`} error={hasError} />
          ))}
        </>
      )}
      <div className="_end" />
    </div>
  )
}
